/**
 * Redirect utilities for post-authentication navigation.
 * Uses URL query parameters instead of localStorage for state preservation.
 */

// prevent open redirect vulnerabilities
const ALLOWED_PATH_PREFIXES = ["/user/", "/onboarding/", "/walmart/", "/mcp/"];

const DEFAULT_REDIRECT = "/user/dashboard";

/**
 * Validates a redirect path is safe to navigate to.
 * Only allows relative paths starting with allowed prefixes.
 */
function isValidRedirectPath(path: string | null | undefined): boolean {
  if (!path || typeof path !== "string") return false;

  if (!path.startsWith("/")) return false;

  // Extract pathname (part before ?) for security checks
  // split always returns at least one element, so pathname is never undefined
  const pathname = path.split("?")[0]!;

  // Block protocol handlers in pathname (javascript:, data:, etc.)
  if (pathname.includes(":")) return false;

  return ALLOWED_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));
}

/**
 * Sanitizes and returns a valid redirect path, or the default.
 */
export function getSafeRedirectPath(path: string | null | undefined): string {
  return isValidRedirectPath(path) ? path! : DEFAULT_REDIRECT;
}

/**
 * Returns `path` if it is a path on this origin, otherwise `fallback`. `navigate({ href })` loads any
 * `https://…`, `//host` or `/\host` href as a new document, so untrusted paths must go through here first.
 */
export function getSameOriginPath(path: string | null | undefined, fallback: string): string {
  return path && /^\/(?![/\\])/.test(path) ? path : fallback;
}
